import styled, { css } from 'styled-components';
import { Table as TableComponent, Row } from '@upgrade/react-components';
import { m, mb, ml, mr, mt, mx, p } from '@upgrade/react-components/Spacing/core';
import Image from '../common/Image';

export const TitleContainers = styled.div`
  display: flex;
  flex-flow: column;
  align-items: center;
  & p {
    ${mb('2xl')};
  }
`;

export const Table = styled(TableComponent)`
  text-align: center;
  tr {
    padding: ${props => props.theme.tableCellPaddingLarge};
    background-color: ${props => props.theme.palette.backgroundPage};
    border-bottom: 1px solid ${props => props.theme.palette.border};
  }
  thead {
    tr {
      th,
      td {
        font-size: ${props => props.theme.typography.sizes.text.mobile.md.fontSize};
        line-height: 1.5rem;
        padding: 0.5rem 0;
        text-align: center;
        @media only screen and (min-width: ${props => props.theme.mediaSm}) {
          padding: 1rem 0.5rem;
          font-size: ${props => props.theme.typography.sizes.text.desktop.lg.fontSize};
          line-height: 1.4rem;
        }
      }
    }
  }
  tr {
    th,
    td {
      ${p('sm')};
      font-size: ${props => props.theme.typography.sizes.text.mobile.md.fontSize};
      min-height: 2rem;
      text-align: center;
      @media only screen and (min-width: ${props => props.theme.mediaSm}) {
        padding: ${props => props.theme.tableCellPaddingLarge};
        font-size: ${props => props.theme.typography.sizes.text.desktop.lg.fontSize};
      }
    }
  }
  td,
  th {
    &:nth-child(2) {
      color: ${props => props.theme.palette.contentReverse};
      font-weight: 500;
      background: ${props => props.theme.palette.actionablePrimary};
    }
    &:last-child {
      font-weight: 500;
    }
  }
  div > p,
  h5 {
    ${m('none')};
  }
  tbody {
    tr:last-child {
      border-bottom: none;
    }
  }
`;

const getBorderRadius = (roundEdgePosition, theme) => (roundEdgePosition ? theme.defaultBorderRadius : 0);

const borderRadiusCss = css`
  border-top-left-radius: ${({ topLeftRounded, theme }) => getBorderRadius(topLeftRounded, theme)};
  border-top-right-radius: ${({ topRightRounded, theme }) => getBorderRadius(topRightRounded, theme)};
  border-bottom-right-radius: ${({ bottomRightRounded, theme }) => getBorderRadius(bottomRightRounded, theme)};
  border-bottom-left-radius: ${({ bottomLeftRounded, theme }) => getBorderRadius(bottomLeftRounded, theme)};
`;

export const TD = styled.td`
  ${borderRadiusCss}
`;

export const TH = styled.th`
  h6 {
    ${m('none')};
  }
  ${borderRadiusCss}
`;

const topBorderCss = css`
  top: -1rem;
  border-radius: 0.5rem 0.5rem 0 0;
`;

const bottomBorderCss = css`
  bottom: -1rem;
  border-radius: 0 0 0.5rem 0.5rem;
`;

const roundedEdgeTableCss = css`
  content: '';
  width: 100%;
  height: 1.4rem;
  background: ${props => props.theme.palette.actionablePrimary};
  right: 0;
  position: absolute;
  ${({ roundedTopEdges }) => roundedTopEdges && topBorderCss}
  ${({ roundedBottomEdges }) => roundedBottomEdges && bottomBorderCss}
`;

export const StyledCurvedTh = styled.th`
  position: relative;

  > span {
    font-size: ${({ theme }) => theme.typography.sizes.text.mobile.xl.fontSize};
    line-height: ${({ theme }) => theme.typography.sizes.text.mobile.xl.lineHeight};
  }
  &:after {
    ${roundedEdgeTableCss}
  }
`;

export const StyledCurvedTd = styled.td`
  position: relative;

  &:after {
    ${roundedEdgeTableCss}
  }
`;

export const StyledTextContainer = styled.div`
  text-align: left;
  font-weight: 300;
  font-size: ${({ theme }) => theme.typography.sizes.text.desktop.xs.fontSize};
  p {
    ${mb('sm')};
  }
  a,
  a:focus,
  a:hover {
    color: inherit;
  }
`;

export const TableContainer = styled.div`
  margin: 0 -1rem 2rem;
  @media (min-width: ${props => props.theme.mediaSm}) {
    ${ml('none')};
    ${mr('none')};
  }
`;

export const HeaderContainer = styled.div`
  font-size: ${({ theme }) => theme.typography.sizes.text.mobile.sm.fontSize};
  line-height: ${({ theme }) => theme.typography.sizes.text.mobile.sm.lineHeight};
  margin-bottom: 0;
`;

export const StyledColGroup = styled.colgroup`
  col {
    :nth-child(1) {
      width: 40%;
    }
    :nth-child(2) {
      width: 30%;
    }
    :nth-child(3) {
      width: 30%;
    }
    @media (min-width: ${props => props.theme.mediaSm}) {
      :nth-child(1) {
        width: 35%;
      }
      :nth-child(2) {
        width: 35%;
      }
      :nth-child(3) {
        width: 30%;
      }
    }
  }
`;

export const StyledContactlessLogos = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  @media (min-width: ${props => props.theme.mediaSm}) {
    justify-content: space-around;
  }
`;

export const StyledContactlessLogo = styled(Image)`
  width: auto;
  height: 3.333rem;
  @media (min-width: ${props => props.theme.mediaSm}) {
    height: 3.75rem;
  }
`;

export const StyledImg = styled(Image)`
  max-width: 100%;
  height: auto;

  @media (min-width: ${props => props.theme.mediaLg}) {
    ${({ withLeftMargin }) => withLeftMargin && `margin-left: 6rem;`};
  }
`;

export const StyledContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: ${({ shouldDisplayMobile }) => (shouldDisplayMobile ? 'block' : `none`)};
  @media (min-width: ${props => props.theme.mediaSm}) {
    display: block;
  }
`;

export const HeroImageContainer = styled.div`
  justify-content: center;
  margin: 0 auto;

  @media (min-width: ${props => props.theme.mediaSm}) {
    display: flex;
  }
`;

export const StyledPromoImage = styled(Image)`
  margin: auto;
  display: block;
  position: absolute;
  top: -2rem;
  left: 0;
  z-index: 2;
  zoom: 1.3;
  transform-style: preserve-3d;
  transform: rotateY(20deg);
`;

export const RewardsText = styled.p`
  width: 3rem;
  @media (min-width: ${props => props.theme.mediaSm}) {
    width: auto;
  }
`;

export const CashBalance = styled.div`
  background: ${props => props.theme.palette.backgroundPrimary};
  ${p('md')};
  border-radius: ${({ theme }) => theme.defaultBorderRadius};
  box-shadow: 1px 1px 16px 0px ${({ theme }) => theme.palette.shadowMedium};
  max-width: fit-content;
  margin: 0 auto;

  ${p('xl')}

  @media (min-width: ${props => props.theme.mediaSm}) {
    ${mx('none')};
    max-width: unset;
  }

  p {
    ${m('none')};
  }

  h3,
  div {
    text-align: left;
  }
`;

export const HeadlineContainer = styled.div`
  text-align: start;
  ${mb('md')};
`;

export const Description = styled.div`
  display: flex;

  img {
    ${mr('md')};
    ${mb('md')};
  }

  @media (min-width: ${props => props.theme.mediaSm}) {
    flex-direction: column;
  }
`;

export const ContactInfoContainer = styled.div`
  .input-text-container {
    overflow: auto;
    label {
      font-size: ${({ theme }) => theme.typography.sizes.text.desktop.sm.fontSize};
    }
  }
`;

export const StyledField = styled.div`
  border: 3px solid ${props => props.theme.palette.accentSecondary1};
  ${p('md')};
  ${mb('sm')};
  color: ${props => props.theme.palette.semanticInfoStrong};
  font-size: ${({ theme }) => theme.typography.sizes.text.desktop.xs.fontSize};
  img {
    display: inline-block;
    vertical-align: middle;
  }
`;

export const OverlayImg = styled.div`
  position: relative;
  top: 5rem;
  z-index: 1;
  p {
    position: absolute;
    top: 1rem;
    left: 0.5rem;
    font-size: ${({ theme }) => theme.typography.sizes.text.desktop.lg.fontSize};

    line-height: 1.125rem;
    text-align: center;
    font-weight: 400;
    color: ${props => props.theme.palette.contentReverse};
    left: -28px;
  }
  img {
    position: absolute;
    left: -40px;
  }
`;

export const StyledContactLink = styled.a`
  display: inline-block;
  text-decoration: none;
  padding: 0 0.5rem;
  ${m('none')};
  width: auto;
  font-size: ${({ theme }) => theme.typography.sizes.text.desktop.xs.fontSize};
  &:hover {
    text-decoration: none;
  }
  @media (min-width: ${props => props.theme.mediaSm}) {
    font-size: ${({ theme }) => theme.typography.sizes.text.desktop.md.fontSize};
  }
`;

export const BackgroundContainer = styled.div`
  background: ${props => props.theme.palette.semanticWarningBackground};
  border-radius: 0.5rem;
  position: relative;
  ${p('md')};
  z-index: 1;

  &::after {
    content: '';
    position: absolute;
    width: 30%;
    height: 100%;
    background: ${props => props.theme.palette.semanticWarning};
    left: 0;
    opacity: 0.5;
    top: 0;
    border-radius: 1rem 0 100% 0;
    z-index: 0;
  }
`;

export const StyledHighlightsContainer = styled.div`
  ${mb('md')};

  li {
    color: ${({ 'data-color': dataColor, theme }) => theme.palette[dataColor] || dataColor || 'initial'};

    path {
      fill: ${({ 'data-color': dataColor, theme }) => `${theme.palette[dataColor] || dataColor} !important`};
    }
  }
`;

export const CenteredContainer = styled.div`
  position: relative;
  margin-left: auto;
  margin-right: auto;
`;

export const StyledTitle = styled.div`
  ${m('none')};
  color: ${({ 'data-color': color = 'inherit', theme }) => theme.palette[color] || color};
  font-size: ${({ theme }) => theme.typography.sizes.headings.mobile.number_lg.fontSize};
  font-weight: 800;
  line-height: 3rem;
  @media (min-width: ${props => props.theme.mediaSm}) {
    font-size: ${({ theme }) => theme.typography.sizes.headings.desktop.number_lg.fontSize};
    line-height: 3.56rem;
  }
  @media (min-width: ${props => props.theme.mediaMd}) {
    font-size: ${({ theme }) => theme.typography.sizes.headings.desktop.number_lg.fontSize};
    line-height: 4.56rem;
    &::after {
      font-size: ${({ theme }) => theme.typography.sizes.text.mobile.xl.fontSize};
      top: -50px;
      right: 55px;
    }
  }
  sup {
    font-size: ${({ theme }) => theme.typography.sizes.headings.desktop.xs.fontSize};
    @media (min-width: ${props => props.theme.mediaMd}) {
      font-size: ${({ theme }) => theme.typography.sizes.headings.desktop.xl.fontSize};
    }
  }
`;

export const StyledCardImg = styled(Image)`
  ${mr('md')};
`;

export const CaptchaWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  ${mb('md')};
`;

export const StyledCardsRow = styled(Row)`
  ${mt('xl')};
  max-width: 56rem;
  row-gap: ${({ theme }) => theme.spacing.md};

  @media (min-width: ${props => props.theme.mediaSm}) {
    margin-left: auto;
    margin-right: auto;
  }
`;

export const StyledSecondaryLinkContainer = styled.div`
  ${mt('md')}
  text-align: center;

  a:hover {
    text-decoration: none;
  }
`;

export const CashSubtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.sizes.text.desktop.xs.fontSize};
  line-height: ${({ theme }) => theme.typography.sizes.text.desktop.xs.lineHeight};
  font-weight: 500;
`;

export const StyledInsuranceNotice = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-top: 0.625rem;
`;

export const StyledInsuranceIcon = styled.div`
  ${mr('sm')};
  margin-bottom: 1px;
`;

export const StyledInsuranceNoticeText = styled.div`
  display: flex;
  justify-content: flex-start;
  color: ${({ theme, 'data-color': dataColor }) => theme.palette[dataColor] || theme.palette.contentWeak};
  font-size: ${({ theme }) => theme.typography.sizes.text.desktop.xs.fontSize};
`;
